import PropTypes from "prop-types";
// eslint-disable-next-line no-restricted-imports
import $ from "jquery";
import React, { useCallback, useEffect, useMemo, useRef } from "react";

import { getProductImageAltText, ProductImageSize } from "../../../helpers/product-image-helpers";
import ProductImage from "../shared/product-image";
import { productImagesPropType, productPropType } from "../../propTypes/product-prop-type";
import { bindZoomWithAsyncCloudZoom } from "../../../helpers/touch-interaction-helper";
import { isSSR } from "../../../helpers/client-server-helper";
import { isImageLoadComplete } from "../../../helpers/dom-helpers";
import { FetchPriority } from "rtr-constants";

const PDPFeaturedImage = ({ imageGroup, isCloudZoomEnabled, product, isMobileViewport = null, index = 0 }) => {
  const cloudZoom = useRef(null);
  const featuredImageRef = useRef(null);
  // We need to know when the featured image has changed to reset the zoom feature (there was a strange bug where the
  // zoom would work, but it would only show the first image in the set when you'd hover over any of them).
  const currentImagePath = imageGroup?.[ProductImageSize.x1080.path];
  // Unfortunately, we need to use jQuery here because CloudZoom requires a jQuery object
  const featuredImageNode = useMemo(() => {
    if (isSSR()) return null;
    return $(featuredImageRef.current);
  }, [currentImagePath, isSSR(), featuredImageRef.current]);

  // @see {https://developer.mozilla.org/en-US/docs/Web/API/HTMLImageElement/complete}
  const isFeaturedImageLoadComplete = useMemo(
    () => isImageLoadComplete(featuredImageRef.current),
    [currentImagePath, featuredImageRef.current?.complete]
  );

  const isImageOutsideViewport = isMobileViewport && index > 1;

  const attachCloudZoom = useCallback(() => {
    bindZoomWithAsyncCloudZoom(CloudZoom => {
      if (cloudZoom?.current) {
        cloudZoom.current.destroy?.();
        cloudZoom.current = null;
      }

      cloudZoom.current = new CloudZoom(featuredImageNode, {
        zoomPosition: "inside",
        zoomOffsetX: 0,
        zoomOffsetY: 0,
        zoomSizeMode: "image",
        zoomFlyOut: false,
        animationTime: 500,
      });
    });
  }, [cloudZoom.current, currentImagePath, isCloudZoomEnabled, featuredImageNode]);

  useEffect(() => {
    // Adding this guard here to wait for the image to finish loading before attaching CloudZoom instance
    // @see {https://developer.mozilla.org/en-US/docs/Web/API/HTMLImageElement/complete}
    if (isCloudZoomEnabled && isFeaturedImageLoadComplete && !cloudZoom.current) {
      attachCloudZoom();
    }

    return () => {
      if (cloudZoom?.current) {
        cloudZoom.current.destroy?.();
        cloudZoom.current = null;
      }
    };
  }, [typeof cloudZoom.current, currentImagePath, isCloudZoomEnabled, isFeaturedImageLoadComplete]);

  return (
    <ProductImage
      altText={getProductImageAltText(product?.displayName, product?.designer?.displayName)}
      className="featured-image"
      imageGroup={imageGroup}
      imageSize={ProductImageSize.x1080}
      srcSetSizes={[ProductImageSize.x480, ProductImageSize.x800, ProductImageSize.x1080]}
      sizes={"(max-width: 480px) 80vw, (max-width: 1024px) 50vw, 1080px"} // ideally the browser would use src as a default if the srcset media width doesn't match, but it's not always smart enough so specificying a size for 1080 here
      item-prop="image"
      ref={featuredImageRef}
      loading={isImageOutsideViewport ? "lazy" : "eager"}
      fetchPriority={isImageOutsideViewport ? FetchPriority.LOW : FetchPriority.HIGH}
    />
  );
};

PDPFeaturedImage.propTypes = {
  imageGroup: productImagesPropType.isRequired,
  isCloudZoomEnabled: PropTypes.bool.isRequired,
  product: productPropType,
  isMobileViewport: PropTypes.bool,
  index: PropTypes.number,
};

export default PDPFeaturedImage;
